import type { ClaimSummary, IRequestResponse, PaginationContext, RecordSchema, SelectOptionContext } from '~/types'

export type RatingContext = {
	id: string
	score: number
	claim_id: string
	customer_id: string
	business_id: string
	comment: null | string
	modal_shown: boolean
	rating_skipped: boolean
	rated: boolean
	created_at: string
	updated_at: string
	deleted_at: null | string
}

export default function useClaimsApi() {
	const getClaimSummary = async () => {
		const { data } = await useAPI<IRequestResponse<ClaimSummary>>('/claims/summary')

		return data
	}
	const fetchClaims = async (query?: RecordSchema) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<ClaimsMetaInformation[]>>>('/claims', {
			query
		})
		return data.value?.data ?? null
	}
	const fetchClaim = async <T = ClaimsMetaInformation>(claim_id?: string) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/claims/${claim_id}`)
		return data.value
	}
	const getTravelIncidentTypes = async () => {
		const { data } = await useAPI<IRequestResponse<string[]>>('/travel/incident-types')
		if (data.value?.data && Array.isArray(data.value.data)) {
			return data.value.data.map(item => ({
				label: item.toLowerCase(),
				value: item
			})) as SelectOptionItem[]
		}
		return []
	}
	const initiateTravelClaim = async (arg: RecordSchema) => {
		const { data } = await useAPI<IRequestResponse<RecordSchema>>('/claims/travel', { body: arg, method: 'POST' })
		return data.value
	}
	const submitTravelClaimsDocuments = async (payload: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/claims/travel/documentation', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const submitIncidentDetails = async (payload: ClaimIncidentPayload) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/claims/gadget', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const submitClaimInspectionRecord = async (payload: ClaimInspectionPayload) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/claims/gadget-inspection', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const submitClaimEstimation = async (payload: ClaimEstimationPayload) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/claims/gadget-estimate-of-repair', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const submitGadgetPreInspection = async (payload: ClaimInspectionPayload) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/inspections/gadget', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const claimsPurchaseUsages = async (purchase_id: string, query?: object) => {
		const { data } = await useAPI<IRequestResponse>(`/claims/purchases/${purchase_id}`, { query })
		return data.value
	}
	const checkPolicyForClaim = async (policy_id: string) => {
		const { data } = await useAPI<IRequestResponse<ClaimsMetaInformation>>(`/claims/policy/${policy_id}`)
		return data.value
	}
	const fetchClaimRatingsByPurchase = async (purchase_id?: string) => {
		const { data } = await useAPI<IRequestResponse<RatingContext>>('/claims/ratings', {
			query: { purchase_id }
		})
		return data.value
	}
	const fetchClaimsRating = async () => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/claims/ratings')
		return data.value
	}
	const fetchClaimRating = async (claim_id: string) => {
		const { data } = await useAPI<IRequestResponse<RatingContext>>(`/claims/${claim_id}/ratings`)
		return data.value
	}
	const rateClaim = async (payload: {
		score: number | null
		comment?: string
		claim_id: string
		rating_id: string
	}) => {
		const { claim_id, rating_id, ...rest } = payload
		const { data } = await useAPI<IRequestResponse<RatingContext>>(`/claims/${claim_id}/ratings/${rating_id}`, {
			body: rest,
			method: 'PATCH'
		})
		return data.value
	}
	const cancelRatingClaim = async (payload: { claim_id: string; rating_id: string }) => {
		const { claim_id, rating_id } = payload
		const { data } = await useAPI<IRequestResponse<RatingContext>>(
			`/claims/${claim_id}/ratings/${rating_id}/cancel`,
			{
				method: 'PATCH'
			}
		)
		return data.value
	}

	return {
		fetchClaimRatingsByPurchase,
		fetchClaims,
		rateClaim,
		fetchClaimRating,
		cancelRatingClaim,
		fetchClaimsRating,
		checkPolicyForClaim,
		fetchClaim,
		getClaimSummary,
		submitIncidentDetails,
		submitClaimInspectionRecord,
		submitClaimEstimation,
		submitGadgetPreInspection,
		getTravelIncidentTypes,
		initiateTravelClaim,
		submitTravelClaimsDocuments,
		claimsPurchaseUsages
	}
}
